import { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  AlertIcon,
  Button,
  Code,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
  Spinner,
} from '@chakra-ui/react'
import axios from 'axios'
import { PhoneCall } from 'lucide-react'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { useAuthStore } from '@/store/auth'

export type AliceQuestionAnswer = {
  question: string
  answer: string
}

export type AliceCallPayload = {
  webhook_source: string
  call_mode: 'new_request' | 'followup'
  client_code: string | null
  call_id: string | null
  ticket: {
    id: number | null
    request_number: string | null
    service_type: string | null
    category: string | null
    description: string | null
    status: string | number | null
  }
  caller: {
    name: string
    contact_number: string
    email: string
  }
  questionnaire: AliceQuestionAnswer[]
}

type Props = {
  isOpen: boolean
  onClose: () => void
  initialPayload: Omit<AliceCallPayload, 'client_code' | 'call_id' | 'caller' | 'questionnaire'> & {
    caller?: Partial<AliceCallPayload['caller']>
    questionnaire?: AliceQuestionAnswer[]
  }
  onSubmitted?: (payload: AliceCallPayload) => void
}

const ALICE_WEBHOOK_URL =
  import.meta.env.VITE_ALICE_WEBHOOK_URL ?? 'http://127.0.0.1:8000/api/webhooks/alice-echo'

export const ALICE_QUESTIONNAIRE = [
  'What is your first and last name?',
  'What is your contact number or email?',
  'What is your location/address?',
  'What exact error message do you see? (If any)',
  'How is this issue affecting your work?',
  'Is anyone else experiencing the same issue?',
  'Is this time-sensitive or business-critical?',
  'One device or multiple devices?',
  'One user or multiple users?',
  'One application or several?',
  'Internal or external access?',
  'Is Work Permit Needed?',
  'How long does it take to process the permit?',
  'Is permit readily available?',
  'May we know your availability for onsite?',
  'May we know if you already approve the parts replacement?',
  'May we close this ticket once the fix is confirmed?',
]

export function AliceCallOutModal({ isOpen, onClose, initialPayload, onSubmitted }: Props) {
  const { showError, showSuccess } = useToastNotification()
  const user = useAuthStore((state) => state.user as any)

  const [responsePreview, setResponsePreview] = useState('')
  const [responseError, setResponseError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const userFullName = useMemo(() => {
    const firstName = String(user?.first_name ?? '').trim()
    const lastName = String(user?.last_name ?? '').trim()
    const detailsFirstName = String(user?.user_details?.first_name ?? '').trim()
    const detailsLastName = String(user?.user_details?.last_name ?? '').trim()
    const detailsCombined = `${detailsFirstName} ${detailsLastName}`.trim()
    const combined = `${firstName} ${lastName}`.trim()
    return (
      combined ||
      detailsCombined ||
      String(user?.name ?? user?.user_login ?? user?.user_details?.nickname ?? '').trim()
    )
  }, [user])

  const userEmail = useMemo(
    () => String(user?.user_email ?? user?.email ?? user?.user_details?.user_email ?? '').trim(),
    [user]
  )

  const userContactNumber = useMemo(
    () =>
      String(
        user?.contact_number ??
          user?.mobile_number ??
          user?.phone_number ??
          user?.phone ??
          user?.user_details?.contact_number ??
          user?.user_details?.mobile_number ??
          user?.user_details?.phone_number ??
          ''
      ).trim(),
    [user]
  )

  const baseQuestionnaire = useMemo(() => {
    const fromPayload = initialPayload.questionnaire ?? []
    const byQuestion = new Map(fromPayload.map((item) => [item.question, item.answer ?? '']))
    return ALICE_QUESTIONNAIRE.map((question) => ({
      question,
      answer: byQuestion.get(question) ?? '',
    }))
  }, [initialPayload.questionnaire])

  useEffect(() => {
    if (!isOpen) return
    setResponsePreview('')
    setResponseError(null)
  }, [isOpen, initialPayload.caller, userFullName, userContactNumber, userEmail])

  const resolvedName = userFullName || initialPayload.caller?.name || ''
  const resolvedContactNumber = userContactNumber || initialPayload.caller?.contact_number || ''
  const resolvedEmail = userEmail || initialPayload.caller?.email || ''

  const payload = useMemo<AliceCallPayload>(() => {
    const questionnaire = baseQuestionnaire.map((item) => ({ ...item }))
    const q1 = questionnaire.find((item) => item.question === 'What is your first and last name?')
    const q2 = questionnaire.find((item) => item.question === 'What is your contact number or email?')
    if (q1) q1.answer = resolvedName || q1.answer
    if (q2) q2.answer = resolvedContactNumber || resolvedEmail || q2.answer

    return {
      webhook_source: initialPayload.webhook_source,
      call_mode: initialPayload.call_mode,
      client_code: user?.default_client_code ?? null,
      call_id: null,
      ticket: initialPayload.ticket,
      caller: {
        name: resolvedName,
        contact_number: resolvedContactNumber,
        email: resolvedEmail,
      },
      questionnaire,
    }
  }, [baseQuestionnaire, initialPayload, resolvedName, resolvedContactNumber, resolvedEmail, user])

  const handleCallNow = async () => {
    if (!resolvedContactNumber && !resolvedEmail) {
      showError(new Error('Contact number or email is required to call out.'))
      setResponseError('Contact number or email is required to call out.')
      return
    }
    setIsSubmitting(true)
    setResponseError(null)
    try {
      const response = await axios.post(ALICE_WEBHOOK_URL, payload)
      setResponsePreview(JSON.stringify(response.data ?? {}, null, 2))
      showSuccess(
        'ALICE call initiated',
        'The ticket will be created or updated automatically once ALICE completes the call.'
      )
      onSubmitted?.(payload)
    } catch (error: any) {
      const errorData = error?.response?.data ?? { message: error?.message ?? 'Request failed' }
      setResponsePreview(JSON.stringify(errorData, null, 2))
      setResponseError(error?.message ?? 'Failed to call ALICE.')
      showError(error)
    } finally {
      setIsSubmitting(false)
    }
  }

  useEffect(() => {
    if (!isOpen) return
    handleCallNow()
    // Intentionally run once per modal open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  return (
    <Modal isOpen={isOpen} onClose={isSubmitting ? () => {} : onClose} size="2xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{isSubmitting ? 'Calling ALICE' : 'ALICE Webhook Response'}</ModalHeader>
        <ModalCloseButton isDisabled={isSubmitting} />
        <ModalBody>
          <VStack align="stretch" spacing={4}>
            {isSubmitting ? (
              <HStack spacing={3}>
                <Spinner size="sm" />
                <Text fontSize="sm">Submitting payload to ALICE webhook...</Text>
              </HStack>
            ) : (
              <>
                <Alert status={responseError ? 'error' : 'success'} borderRadius="md">
                  <AlertIcon />
                  <Text fontSize="sm">
                    {responseError
                      ? 'Request failed. Showing error response payload.'
                      : 'Request submitted successfully. Showing JSON response payload.'}
                  </Text>
                </Alert>

                <Code
                  as="pre"
                  display="block"
                  whiteSpace="pre-wrap"
                  p={3}
                  borderRadius="md"
                  fontSize="xs"
                  maxH="400px"
                  overflowY="auto"
                >
                  {responsePreview || '{}'}
                </Code>

                <VStack align="stretch" spacing={2}>
                  <Text fontSize="xs" fontWeight="semibold">Resolved Caller Data</Text>
                  <HStack justify="space-between" align="start">
                    <Text fontSize="xs" color="gray.600">Name</Text>
                    <Text fontSize="xs" textAlign="right">{resolvedName || '-'}</Text>
                  </HStack>
                  <HStack justify="space-between" align="start">
                    <Text fontSize="xs" color="gray.600">Contact Number</Text>
                    <Text fontSize="xs" textAlign="right">{resolvedContactNumber || '-'}</Text>
                  </HStack>
                  <HStack justify="space-between" align="start">
                    <Text fontSize="xs" color="gray.600">Email</Text>
                    <Text fontSize="xs" textAlign="right">{resolvedEmail || '-'}</Text>
                  </HStack>
                </VStack>
              </>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose} isDisabled={isSubmitting}>
            {isSubmitting ? 'Please wait...' : 'Close'}
          </Button>
          {!isSubmitting && (
            <Button
              leftIcon={<PhoneCall size={16} />}
              colorScheme="green"
              variant="outline"
              onClick={handleCallNow}
            >
              Retry
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

